import React, { useCallback, useMemo } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ViewsIcon from '@atlaskit/icon-lab/core/views';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag/removable-tag';
import { token } from '@atlaskit/tokens';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';

import { parseViewJql } from './parseViewJql';
import { useHydratedFilterUsers } from './useHydratedFilterUsers';
import { ViewFilterChips, ViewGroupChip, ViewSortChip } from './ViewFilterChips';

const styles = cssMap({
	rovoHint: {
		paddingTop: token('space.250'),
		paddingLeft: token('space.150'),
	},
});

// Literal JQL emits for the `currentUser()` function; substituted with the viewer's aaid before hydration.
const CURRENT_USER_JQL_LITERAL = 'currentUser()';

// JQL fields whose values are user identifiers. Only these are worth resolving via the
// user-hydration lookup; other fields' literals (dates, labels, priorities, etc) are not accountIds.
const USER_FIELDS = new Set(['assignee', 'reporter', 'creator', 'voter', 'watcher', 'watchers']);

const cardTitle = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.card-title',
	defaultMessage: 'AI view create',
	description:
		'Title shown in the header of the confirmation card for creating and configuring a new Jira view.',
});

const confirmButton = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.confirm-button',
	defaultMessage: 'Create view',
	description: 'Label for the button that confirms creating a new list in Jira.',
});

const dismissButton = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.dismiss-button',
	defaultMessage: 'Dismiss',
	description: 'Label for the button that dismisses the create view confirmation card.',
});

const filterLabel = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.filter-label',
	defaultMessage: 'Filter',
	description:
		'Bold label shown above the filter clauses that will be applied to the view being created in Jira.',
});

const jqlLabel = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.jql-label',
	defaultMessage: 'JQL',
	description:
		'Bold label shown above the raw JQL query string when it is too complex to render as individual filter chips.',
});

const groupByLabel = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.group-by-label',
	defaultMessage: 'Group',
	description: 'Bold label shown above the field that the view being created will be grouped by.',
});

const sortByLabel = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.sort-by-label',
	defaultMessage: 'Sort',
	description: 'Bold label shown above the field that the view being created will be sorted by.',
});

const hideDoneLabel = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.hide-done-label',
	defaultMessage: 'Hide done',
	description:
		'Bold label shown above an indicator of whether done items are hidden in the view being created.',
});

const showHierarchyLabel = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.show-hierarchy-label',
	defaultMessage: 'Show hierarchy',
	description:
		'Bold label shown above an indicator of whether the parent/child hierarchy is shown in the view being created.',
});

const yesValue = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.yes-value',
	defaultMessage: 'Yes',
	description:
		'Chip text shown under the "Hide done" label when that option is enabled for the view being created.',
});

const rovoHint = defineMessage({
	id: 'conversation-assistant-message-actions.jira-view-create-and-configure.rovo-hint',
	defaultMessage: 'Use Rovo to make more changes to your view.',
	description: 'Helper text shown below the create-view confirmation card.',
});

export const JiraViewCreateAndConfigureAction = ({
	actions,
	getStatus,
	getReadonly,
	onConfirm,
	onCancel,
	cloudId,
	aaid,
}: ActionRendererFor<typeof ActionKeys.JiraViewCreateAndConfigure>): JSX.Element | null => {
	const firstAction = actions[0];

	const { formatMessage } = useIntl();

	const handleConfirm = useCallback(() => {
		void onConfirm(firstAction!);
	}, [firstAction, onConfirm]);

	const handleDismiss = useCallback(() => {
		void onCancel(firstAction!);
	}, [firstAction, onCancel]);

	const jql = firstAction?.data.jql;
	const {
		clauses: parsedClauses,
		isComplex: isJqlComplex,
		sortBy,
	} = useMemo(
		() => (jql ? parseViewJql(jql) : { clauses: [], isComplex: false, sortBy: undefined }),
		[jql],
	);
	// True when the JQL is structurally too complex to represent as filter chips (compound
	// AND/OR nesting, NOT), or when parsing produced no usable clauses at all. In both cases,
	// fall back to rendering the raw JQL string instead of silently rendering nothing.
	const shouldRenderRawJql = Boolean(jql) && (isJqlComplex || parsedClauses.length === 0);

	const idsToHydrate = useMemo(() => {
		const literals = parsedClauses
			.filter((clause) => USER_FIELDS.has(clause.field.toLowerCase()))
			.flatMap((clause) => clause.value);
		const substituted = literals.map((literal) =>
			literal.toLowerCase() === CURRENT_USER_JQL_LITERAL.toLowerCase() && aaid ? aaid : literal,
		);
		return Array.from(new Set(substituted));
	}, [parsedClauses, aaid]);

	const { users: hydratedUsers } = useHydratedFilterUsers(idsToHydrate, cloudId);

	const hydratedUserNamesById = useMemo(() => {
		const map = new Map<string, string>();
		for (const user of hydratedUsers) {
			map.set(user.id, user.name);
		}
		if (aaid) {
			const viewerName = map.get(aaid);
			if (viewerName) {
				// JQL function names are case-insensitive, so map every casing variant of
				// currentUser() actually present in the clauses, not just the canonical spelling.
				const currentUserLiterals = new Set(
					parsedClauses
						.filter((clause) => USER_FIELDS.has(clause.field.toLowerCase()))
						.flatMap((clause) => clause.value)
						.filter((literal) => literal.toLowerCase() === CURRENT_USER_JQL_LITERAL.toLowerCase()),
				);
				for (const literal of currentUserLiterals) {
					map.set(literal, viewerName);
				}
			}
		}
		return map;
	}, [hydratedUsers, aaid, parsedClauses]);

	if (!firstAction) {
		return null;
	}

	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);

	const isFinished = status === 'finished';
	const isInProgress = status === 'started';

	const { groupBy, hideDone, hierarchyEnabled, fields } = firstAction.data;

	return (
		<>
			<ActionCardWrapper
				product="jira"
				title={formatMessage(cardTitle)}
				defaultExpanded
				isTitleBold
				invertChevron
				headerIcon={<ViewsIcon label="" />}
			>
				<Stack space="space.150">
					{jql && (
						<Stack space="space.050">
							<Text weight="bold" size="small" color="color.text.subtle">
								{formatMessage(shouldRenderRawJql ? jqlLabel : filterLabel)}
							</Text>
							{shouldRenderRawJql ? (
								<Text size="small" color="color.text.subtle">
									{jql}
								</Text>
							) : (
								<ViewFilterChips
									clauses={parsedClauses}
									fields={fields}
									hydratedUserNamesById={hydratedUserNamesById}
								/>
							)}
						</Stack>
					)}

					{groupBy && (
						<Stack space="space.050">
							<Text weight="bold" size="small" color="color.text.subtle">
								{formatMessage(groupByLabel)}
							</Text>
							<ViewGroupChip groupBy={groupBy} fields={fields} />
						</Stack>
					)}

					{sortBy && !shouldRenderRawJql && (
						<Stack space="space.050">
							<Text weight="bold" size="small" color="color.text.subtle">
								{formatMessage(sortByLabel)}
							</Text>
							<ViewSortChip field={sortBy.field} direction={sortBy.direction} fields={fields} />
						</Stack>
					)}

					{hideDone && (
						<Stack space="space.050">
							<Text weight="bold" size="small" color="color.text.subtle">
								{formatMessage(hideDoneLabel)}
							</Text>
							<Inline>
								<Tag
									text={formatMessage(yesValue)}
									isRemovable={false}
									color="standard"
									hasMargin={false}
								/>
							</Inline>
						</Stack>
					)}

					{hierarchyEnabled && (
						<Stack space="space.050">
							<Text weight="bold" size="small" color="color.text.subtle">
								{formatMessage(showHierarchyLabel)}
							</Text>
							<Inline>
								<Tag
									text={formatMessage(yesValue)}
									isRemovable={false}
									color="standard"
									hasMargin={false}
								/>
							</Inline>
						</Stack>
					)}

					{!readonly && !isFinished && (
						<ActionCardFooter testId="jira-view-create-and-configure-footer">
							<Inline space="space.100" alignInline="end">
								<Button
									appearance="subtle"
									onClick={handleDismiss}
									isDisabled={isInProgress}
									testId="jira-view-create-and-configure-dismiss-button"
								>
									{formatMessage(dismissButton)}
								</Button>
								<Button
									appearance="primary"
									onClick={handleConfirm}
									isDisabled={isInProgress}
									testId="jira-view-create-and-configure-confirm-button"
								>
									{formatMessage(confirmButton)}
								</Button>
							</Inline>
						</ActionCardFooter>
					)}
				</Stack>
			</ActionCardWrapper>
			<Box xcss={styles.rovoHint}>
				<Text color="color.text.subtle">{formatMessage(rovoHint)}</Text>
			</Box>
		</>
	);
};
